/**
 * Types for the email drafts API (issue #8). Wire shape is snake_case, mirroring
 * `backend/apps/campaigns/serializers.py` verbatim.
 */

export type CampaignsMode = "off" | "test";

/** `GET /api/campaigns/status` — the gate, and in test mode who may receive. */
export interface CampaignsStatus {
  mode: CampaignsMode;
  reason: string;
  /** The test allow-list: the only possible recipients. Empty when off. */
  recipients: string[];
  from_email: string;
}

export type DraftKind = "broadcast" | "sequence";

// --- the email builder (#740): `apps.campaigns.builder` ----------------------------------------

export type Align = "left" | "center";

export type EmailBlock =
  | { id: string; type: "heading"; text: string; level: 1 | 2; align: Align }
  /** `html`: bold, italic, links and bullet lists; merge fields as ActiveCampaign tokens (`%FIRSTNAME%`). */
  | { id: string; type: "text"; html: string }
  /** `width`: the uploaded image's width, so a small one is never stretched; null for an address pasted in. */
  | { id: string; type: "image"; src: string; alt: string; href: string; full: boolean; width: number | null }
  | { id: string; type: "button"; label: string; href: string; /** The fill: MAC's maroon or its teal. */ tone: "maroon" | "teal"; align: Align }
  | { id: string; type: "divider" }
  | { id: string; type: "spacer"; size: "small" | "medium" | "large" }
  | { id: string; type: "columns"; left: string; right: string };

export type BlockType = EmailBlock["type"];

/** A builder email: what the server renders into `body_html`, the preview and every test send. */
export interface EmailDoc {
  version: 1;
  /** The line an inbox shows after the subject. */
  preheader: string;
  blocks: EmailBlock[];
}

/** `POST /api/campaigns/render`: the one renderer, on blocks not yet saved. */
export interface RenderedEmail {
  html: string;
  text: string;
}

/** `POST /api/campaigns/images`: where mail clients fetch the uploaded image, and its size. */
export interface UploadedImage {
  url: string;
  width: number;
  height: number;
}

export interface DraftStep {
  position: number;
  subject: string;
  /** A builder email's body is rendered from `blocks`; a raw-HTML email's is what staff wrote, sanitised. */
  body_html: string;
  /** The builder's blocks (#740), or null for an email written as raw HTML (it stays raw). */
  blocks: EmailDoc | null;
  /** Minutes after the previous email is sent. Always 0 on the first. */
  delay_minutes: number;
  /** The generated plain-text part; always ends with the unsubscribe link. */
  text_preview: string;
  /** The HTML part as a test send carries it, the unsubscribe footer added (#716). */
  html_preview: string;
}

export type SendStepStatus =
  | "queued"
  | "building"
  | "enabling"
  | "scheduled"
  | "sent"
  | "cleaned_up"
  | "failed"
  | "cancelled"
  | "cleanup_error";

export interface TestSendStep {
  position: number;
  subject: string;
  delay_minutes: number;
  status: SendStepStatus;
  due_at: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  cleaned_at: string | null;
  send_amt: number | null;
  ac_campaign_id: string;
  ac_message_id: string;
  failure_reason: string;
  alert: string;
  cleanup_error: string;
}

export interface TestSend {
  id: number;
  approved_by_name: string;
  approved_at: string;
  recipient_count: number;
  status: "in_progress" | "failed" | "cancelled" | "cleaned_up";
  steps: TestSendStep[];
}

export interface Draft {
  id: number;
  name: string;
  kind: DraftKind;
  from_name: string;
  reply_to: string;
  /** Who it is for (#716): a People filter in the member list's parameters, `{}` for everyone. */
  audience: Record<string, string>;
  /** The audience's name as chosen ("Active members"); "" when none was chosen. */
  audience_name: string;
  steps: DraftStep[];
  sends: TestSend[];
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

/** Create/replace payload. Steps are written in order; positions are server-set. */
export interface DraftInput {
  name: string;
  kind: DraftKind;
  from_name: string;
  reply_to: string;
  audience: Record<string, string>;
  audience_name: string;
  /** A builder email sends `blocks` (its `body_html` is the server's to render); a raw one sends `body_html` and null. */
  steps: { subject: string; body_html: string; blocks: EmailDoc | null; delay_minutes: number }[];
}

/** Why an email leaves someone out (#716), in the order they are tested. */
export const LEFT_OUT = ["merged", "deceased", "added_in_keystone", "no_email", "invalid_email", "opted_out", "bounced", "no_consent", "not_in_ac"] as const;
export type LeftOut = (typeof LEFT_OUT)[number];

/** `GET /api/campaigns/audience?<member filters>` (#716): who the filter lists, who an email reaches, who each reason leaves out. */
export interface AudienceCounts {
  matched: number;
  reachable: number;
  leftOut: Record<LeftOut, number>;
}

// --- licence reminders (#339): `apps.campaigns.views.LicenceRemindersView`, camelCase ---------------

/** Tim's schedule (28 Oct): 3, 2 and 1 months before the licence expires, and 1 month after. Everyone gets every step. */
export const LICENCE_REMINDER_STEPS = ["three_months", "two_months", "one_month", "after_expiry"] as const;
export type LicenceReminderStepKey = (typeof LICENCE_REMINDER_STEPS)[number];

/**
 * Why a person in a step's window is held back, in the order they are tested: every email's reasons, then the licence's
 * own (not on the latest state list; suspended or limited by the state; a month after expiry only, a lapse no later list
 * confirms yet).
 */
export const LICENCE_REMINDER_HELD = [...LEFT_OUT, "not_on_state_list", "suspended_limited", "expiry_unconfirmed"] as const;
export type LicenceReminderHeld = (typeof LICENCE_REMINDER_HELD)[number];

export interface LicenceReminderStep {
  key: LicenceReminderStepKey;
  label: string;
  /** The licence expiry dates the step is open for today (ISO, inclusive). */
  window: { from: string; to: string };
  /** People due: in the window and not held back. `filter` lists them. */
  due: number;
  held: Partial<Record<LicenceReminderHeld, number>>;
  /** Due and not yet reminded this licence cycle: the next run's would-send count. */
  toSend: number;
  filter: { licenceReminder: LicenceReminderStepKey };
  /** The email as members would see it, with sample merge values. */
  preview: { subject: string; html: string; text: string; fromName: string };
}

export interface LicenceReminders {
  asOf: string;
  /** The daily run's switch (`MAC_LICENCE_REMINDERS_MODE`). */
  mode: "off" | "test";
  modeReason: string;
  /** Email drafts test mode (#8): whether "Send test" can send. */
  testSends: boolean;
  /** The test allow-list (empty unless test sends are on). */
  recipients: string[];
  lastRun: { at: string; status: "ok" | "ceiling"; enrolled: number; previews: number; alert: string } | null;
  steps: LicenceReminderStep[];
}

export interface LicenceReminderTestSend {
  id: number;
  step: LicenceReminderStepKey;
  recipients: string[];
}
